import type { apiTypes } from '@cmg/common';
import { Button, DialogContentText } from '@cmg/design-system';
import type { FC } from 'react';
import { Fragment, useEffect, useState } from 'react';

import usePrevious from '../../../../../../common/util/usePrevious';
import { ServerErrorAlert } from '../../../../../../design-system/alert/ServerErrorAlert';
import { ConfirmationDialog } from '../../../../../../design-system/dialog/confirmation/ConfirmationDialog';
import { FormDialogContent } from '../../../../../../design-system/dialog/FormDialogContent';
import { FormDialogContentItem } from '../../../../../../design-system/dialog/FormDialogContentItem';

export type Props = Readonly<{
  onReset: () => void;
  isLoading: Readonly<boolean>;
  error: Readonly<apiTypes.GenericServerError | null>;
  onUnlockUser: () => void;
}>;

export const UnlockUserAction: FC<Props> = ({ onReset, isLoading, error, onUnlockUser }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const previousIsLoading = usePrevious(isLoading);
  useEffect(() => {
    if (previousIsLoading && !isLoading && !error) {
      setIsDialogOpen(false);
    }
  }, [error, isLoading, previousIsLoading]);

  const previousIsDialogOpen = usePrevious(isDialogOpen);
  useEffect(() => {
    if (previousIsDialogOpen && !isDialogOpen) {
      onReset();
    }
  }, [isDialogOpen, onReset, previousIsDialogOpen]);

  return (
    <Fragment>
      <Button
        variant="outlined"
        color="primary"
        disabled={isLoading}
        onClick={() => setIsDialogOpen(true)}
      >
        Unlock User
      </Button>
      <ConfirmationDialog
        isOpen={isDialogOpen}
        isLoading={isLoading}
        title="Unlock User"
        cancelButtonLabel="Cancel"
        onCancel={() => setIsDialogOpen(false)}
        submitButtonLabel="Unlock User"
        submitButtonColor="primary"
        onSubmit={() => onUnlockUser()}
        content={
          <FormDialogContent
            error={error && <ServerErrorAlert title="Error" error={error} />}
            items={
              <FormDialogContentItem
                value={
                  <DialogContentText>
                    User will be unlocked. Are you sure you want to proceed?
                  </DialogContentText>
                }
              />
            }
          />
        }
      />
    </Fragment>
  );
};
